import React from "react";
import { Card } from "../../ui/Card";
import { Users, LogIn, TrendingUp, TrendingDown, Compass, Calendar } from "lucide-react";

export function AtarimKpiCards({
  currentOccupancy = 0,
  todayStats = { total: 0, person: 0, micromobility: 0 },
  hourlyRushHourData = [],
  locationRushHourData = [],
  location = "",
  weeklyVisitorsRushHourData = 0,
  weeklyVisitorsTrend = null,
  monthlyRushHourData = 0,
}) {
  // Peak hour 24h
  const peakItem24 = hourlyRushHourData && hourlyRushHourData.length > 0 ? hourlyRushHourData[0] : null;
  const peakHourStr24 = peakItem24?.date
    ? `${new Date(peakItem24.date).getHours()}:00 - ${new Date(peakItem24.date).getHours() + 1}:00`
    : "--:--";
  const peakDateStr24 = peakItem24?.date
    ? new Date(peakItem24.date).toLocaleDateString("he-IL", { weekday: "short", day: "numeric", month: "numeric" })
    : "";
  const peakVisitors24 = peakItem24 ? Math.round(peakItem24.maxEntering || peakItem24.entering || 0) : 0;

  // Busiest entrance
  const locItem = locationRushHourData && locationRushHourData.length > 0 ? locationRushHourData[0] : null;
  const busiestName = location || locItem?.location || "כניסה ראשית";
  const busiestCount = locItem ? Math.round(locItem.inCount || locItem.entering || 0) : 0;

  return (
    <div dir="rtl" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 w-full mb-4 text-right">
      {/* 1. תפוסה נוכחית */}
      <Card className="flex flex-col justify-between p-3.5 bg-gradient-to-br from-white to-blue-50/40 border border-blue-100 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
            <span className="text-xs font-semibold text-gray-700">תפוסה נוכחית</span>
          </div>
          <div className="p-1.5 rounded-lg bg-emerald-100/70 text-emerald-700">
            <Users className="h-4 w-4" />
          </div>
        </div>
        <div className="my-1.5 text-right">
          <div className="text-2xl lg:text-3xl font-extrabold text-gray-900 tracking-tight">
            {Number(currentOccupancy).toLocaleString()}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            אנשים כעת במתחם
          </div>
        </div>
      </Card>

      {/* 2. כניסות היום */}
      <Card className="flex flex-col justify-between p-3.5 bg-gradient-to-br from-white to-indigo-50/40 border border-indigo-100 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-700">כניסות היום</span>
          <div className="p-1.5 rounded-lg bg-indigo-100/70 text-indigo-700">
            <LogIn className="h-4 w-4" />
          </div>
        </div>
        <div className="my-1.5 text-right">
          <div className="text-2xl lg:text-3xl font-extrabold text-indigo-900 tracking-tight">
            {Number(todayStats.total).toLocaleString()}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            הולכי רגל: <span className="font-semibold text-gray-700">{Number(todayStats.person).toLocaleString()}</span> | מיקרו׳: <span className="font-semibold text-gray-700">{Number(todayStats.micromobility).toLocaleString()}</span>
          </div>
        </div>
      </Card>

      {/* 3. שעת שיא (24 שעות) */}
      <Card className="flex flex-col justify-between p-3.5 bg-gradient-to-br from-white to-amber-50/40 border border-amber-100 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-700">שעת שיא (24 שעות)</span>
          <div className="p-1.5 rounded-lg bg-amber-100/70 text-amber-700">
            <TrendingUp className="h-4 w-4" />
          </div>
        </div>
        <div className="my-1.5 text-right">
          <div className="text-2xl lg:text-3xl font-extrabold text-amber-900 tracking-tight">
            {peakVisitors24 > 0 ? peakVisitors24.toLocaleString() : "--"}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            {peakHourStr24} {peakDateStr24 && `(${peakDateStr24})`}
          </div>
        </div>
      </Card>

      {/* 4. הכניסה העמוסה ביותר */}
      <Card className="flex flex-col justify-between p-3.5 bg-gradient-to-br from-white to-purple-50/40 border border-purple-100 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-700">כניסה מובילה</span>
          <div className="p-1.5 rounded-lg bg-purple-100/70 text-purple-700">
            <Compass className="h-4 w-4" />
          </div>
        </div>
        <div className="my-1.5 text-right">
          <div className="text-lg lg:text-xl font-extrabold text-purple-900 truncate">
            {busiestName}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            {busiestCount > 0 ? `${busiestCount.toLocaleString()} כניסות בשבוע שעבר` : "בשבוע החולף"}
          </div>
        </div>
      </Card>

      {/* 5. מבקרים בשבוע שעבר */}
      <Card className="flex flex-col justify-between p-3.5 bg-gradient-to-br from-white to-cyan-50/40 border border-cyan-100 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-700">מבקרים בשבוע שעבר</span>
          <div className="p-1.5 rounded-lg bg-cyan-100/70 text-cyan-700">
            <Calendar className="h-4 w-4" />
          </div>
        </div>
        <div className="my-1.5 text-right">
          <div className="flex items-center justify-between gap-2">
            <div className="text-2xl lg:text-3xl font-extrabold text-cyan-900 tracking-tight">
              {weeklyVisitorsRushHourData > 0 ? Math.round(weeklyVisitorsRushHourData).toLocaleString() : "--"}
            </div>
            {weeklyVisitorsTrend !== null && weeklyVisitorsTrend !== undefined && (
              <div
                className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-bold ${
                  weeklyVisitorsTrend >= 0
                    ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                    : "bg-rose-100 text-rose-700 border border-rose-200"
                }`}
                title={`שינוי בהשוואה לשבוע שלפני: ${weeklyVisitorsTrend >= 0 ? "+" : ""}${weeklyVisitorsTrend}%`}
              >
                {weeklyVisitorsTrend >= 0 ? (
                  <TrendingUp className="h-3.5 w-3.5" />
                ) : (
                  <TrendingDown className="h-3.5 w-3.5" />
                )}
                <span>{weeklyVisitorsTrend >= 0 ? `+${weeklyVisitorsTrend}%` : `${weeklyVisitorsTrend}%`}</span>
              </div>
            )}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            {monthlyRushHourData > 0 ? `בחודש החולף: ${Math.round(monthlyRushHourData).toLocaleString()}` : "סך כל המבקרים"}
          </div>
        </div>
      </Card>
    </div>
  );
}

export default AtarimKpiCards;
